import React, { useEffect, useState } from 'react';
import { SmartSendItemsCache } from '~/apps/smartsend/lib/cache/SmartSendItemsCache';
import { MarketplaceSupportsMarkingAsDispatched } from '~/apps/smartsend/lib/helpers/MarketplaceSupportsMarkingAsDispatched';
import { MarketplaceDispatchItemDetailsModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { P2GAlert, P2GBox, P2GLink, P2GProgress, P2GText, SvgConfirmCircle } from '@parcel2go/core-ui';
import AlertWithIcon from '../../AlertWithIcon';
import { AsyncLinks } from './book/AsyncLinks';

interface props {
  items: SmartSendItemApiModel[];
  markAsDispatched: () => void;
  wrapper: (x: React.ReactNode) => React.ReactElement;
}

const GetItemStatus = (item: SmartSendItemApiModel, statuses: MarketplaceDispatchItemDetailsModel[]) => {
  const status = statuses.find((s) => s.MarketplaceOrderlineDispatches.some((m) => m.ParentImportedItemId === item.Id));
  if (status) {
    return status.OverallDispatchStatus;
  }
  return SmartSendItemsCache.find((x) => x.Id === item.Id)?.MarketplaceStatus ?? item.MarketplaceStatus;
};

const ItemIsDispatched = (item: SmartSendItemApiModel, statuses: MarketplaceDispatchItemDetailsModel[]) =>
  GetItemStatus(item, statuses) === 'Dispatched';

const OrderBookingStatusObserver: React.FC<props> = (props) => {
  const { getItemsMarketplaceDispatchStatus } = useSmartSendApi();

  // Unique Items
  const uniqueItems = Array.from(new Map(props.items.map((item) => [item.Id, item])).values());

  // State
  const [takingTooLong, setTakingTooLong] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [viewDetals, setViewDetails] = useState(false);

  // API Statuses
  const [statuses, setStatuses] = useState<MarketplaceDispatchItemDetailsModel[]>([]);
  const [hasQueried, setHasQueried] = useState(false);

  // Items
  const itemsToConsider = uniqueItems.filter((x) => MarketplaceSupportsMarkingAsDispatched(x));
  const ids = itemsToConsider.map((x) => x.Id);
  const hasOrdersWhichCanBeMarkedAsDispatched = itemsToConsider.length > 0;
  const itemsCount = itemsToConsider.length;

  // Is This Automatic Dispatch?
  const isAutomatic =
    statuses.length > 0 && statuses.every((x) => x.MarketplaceOrderlineDispatches?.every((y) => y.IsAutomaticDispatch));

  const dispatchedItems = itemsToConsider.filter((x) => ItemIsDispatched(x, statuses));
  const allItemsDispatched = dispatchedItems.length === itemsCount;

  const itemSummary = itemsToConsider.map((x) => ({
    Id: x.Id,
    MarketplaceStatus: GetItemStatus(x, statuses)
  }));

  const itemSummaryGroupedByMarketplaceStatue = itemSummary.reduce((acc, item) => {
    const status = item.MarketplaceStatus;
    if (!acc[status]) {
      acc[status] = 0;
    }
    acc[status]++;
    return acc;
  }, {});

  // Customer Wants to do this manually
  const trigger = () => {
    if (props.markAsDispatched) {
      props.markAsDispatched();
    }
    setTriggered(true);
    setViewDetails(false);
  };

  // Just for messaging
  useEffect(() => {
    const timer = setTimeout(() => {
      setTakingTooLong(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (itemsToConsider.length == 0 || allItemsDispatched) {
      return;
    }
    const run = async () => {
      if (ids.length > 0) {
        const res = await getItemsMarketplaceDispatchStatus(ids);
        if (res.success && res.result) {
          setStatuses(res.result);
          if (res.result.length > 0) {
            setHasQueried(true);
          }
        }
      }
    };
    run(); // Call run immediately
    const interval = setInterval(run, 15_000);
    return () => clearInterval(interval);
  }, []);

  if (!hasOrdersWhichCanBeMarkedAsDispatched || itemsToConsider.length === 0) {
    return null;
  }

  if (allItemsDispatched) {
    return props.wrapper(
      <P2GBox>
        <P2GBox mb={2}>
          <P2GText component="p" colour="primary">
            <strong>Marketplace Tracking</strong>
          </P2GText>
        </P2GBox>
        <AlertWithIcon icon={<SvgConfirmCircle />} color="success">
          <P2GText variant="body2">All e-commerce platforms have been updated with tracking numbers.</P2GText>
        </AlertWithIcon>
      </P2GBox>
    );
  }

  if (triggered) {
    return props.wrapper(
      <P2GBox>
        <P2GBox mb={2}>
          <P2GText component="p" colour="primary">
            <strong>Marketplace Tracking</strong>
          </P2GText>
        </P2GBox>
        <AlertWithIcon icon={<P2GProgress />} color="info">
          <P2GText variant="body2" component="p">
            We are still <P2GLink onClick={() => setViewDetails((x) => !x)}>updating</P2GLink>{' '}
            {itemsCount - dispatchedItems.length} of your items with tracking details.
          </P2GText>
          {viewDetals && (
            <P2GBox mt={1}>
              {Object.keys(itemSummaryGroupedByMarketplaceStatue).map((x) => (
                <P2GText variant="body2" component="p" key={x}>
                  {x} : {itemSummaryGroupedByMarketplaceStatue[x]}
                </P2GText>
              ))}
            </P2GBox>
          )}
        </AlertWithIcon>
      </P2GBox>
    );
  }

  if (isAutomatic) {
    return props.wrapper(
      <P2GBox>
        <P2GBox mb={2}>
          <P2GText component="p" colour="primary">
            <strong>Marketplace Tracking</strong>
          </P2GText>
        </P2GBox>
        <AlertWithIcon icon={<P2GProgress />} color="success">
          <P2GText variant="body2" component="p">
            Your items will <P2GLink onClick={() => setViewDetails((x) => !x)}>automatically</P2GLink> notify your
            e-commerce platform with tracking numbers. You can leave this page, and we will update them as soon as they
            become available.
          </P2GText>
          {viewDetals && (
            <P2GBox mt={1}>
              {Object.keys(itemSummaryGroupedByMarketplaceStatue).map((x) => (
                <P2GText variant="body2" component="p" key={x}>
                  {x} : {itemSummaryGroupedByMarketplaceStatue[x]}
                </P2GText>
              ))}
              {takingTooLong && (
                <P2GBox mt={1}>
                  <P2GText variant="body2" component="p">
                    You can opt to update your e-commerce platform manually if you prefer, but this will not include the
                    courier specific consignment numbers and may not be trackable in the e-commerce platform.
                    <br />
                    <P2GLink onClick={trigger}>Update Anyway</P2GLink>
                  </P2GText>
                </P2GBox>
              )}
            </P2GBox>
          )}
        </AlertWithIcon>
      </P2GBox>
    );
  }

  if (!hasQueried) {
    return props.wrapper(
      <P2GBox>
        <P2GBox mb={2}>
          <P2GText component="p" colour="primary">
            <strong>Marketplace Tracking</strong>
          </P2GText>
        </P2GBox>
        <AlertWithIcon icon={<P2GProgress />} color="info">
          <P2GText variant="body2">We're just checking the status of your updates...</P2GText>
        </AlertWithIcon>
      </P2GBox>
    );
  }

  return props.wrapper(
    <P2GBox>
      <P2GBox mb={2}>
        <P2GText component="p" colour="primary">
          <strong>Marketplace Tracking</strong>
        </P2GText>
      </P2GBox>
      <P2GAlert color="info">
        <AsyncLinks
          variant="list"
          links={[
            {
              Type: 'MARKDISPATCHED_ONCLICK',
              Label: 'Update',
              Href: null,
              OnClick: trigger,
              HideIcon: true
            }
          ]}
          match={null}
          tag={''}
          title={''}
        />
      </P2GAlert>
    </P2GBox>
  );
};

export default OrderBookingStatusObserver;
